import { isStagingType } from '@shared-lib/staging';

import {
  ELECTRICAL_DEVICES,
  isCeilingElectricalDevice,
} from '../utils/electricalDevices';

/**
 * Constants for elevation viewer object filtering
 */

/**
 * Ground-mounted object types
 * These objects sit on the floor and should be shown in elevations
 * if any edge is within GROUND_MOUNTED_PROXIMITY_THRESHOLD of the wall
 */
export const GROUND_MOUNTED_OBJECT_TYPES = [
  // Bathroom fixtures
  'toilet',
  'sink',
  'vanity',
  'bathtub',
  'shower',
  'shower-door',

  // Faucets (mounted on ground-mounted fixtures)
  'bathroom-faucet',
  'bathroom-sink-faucet',

  // Furniture
  'storage',
  'chair',
  'table',
  'sofa',
  'bed',

  // Kitchen appliances — both old (stove / oven) and new (range / wall-oven)
  // accepted so saved structures from before the GLB-aligned type rename
  // still classify correctly.
  'refrigerator',
  'stove',
  'range',
  'cooktop',
  'oven',
  'wall-oven',
  'dishwasher',
];

/**
 * Wall-mounted object types
 * These objects attach to walls and should only be shown in elevations
 * if the parent wall is the currently shown wall
 */
export const WALL_MOUNTED_OBJECT_TYPES = [
  'mirror',
  'wall-light',
  'overhead-wall-light',
  'towel-bar',
  'wall-cabinet',
  // Both old (range-hood) and new (vent-hood) accepted.
  'range-hood',
  'vent-hood',
  'wall-art',
  'shelf',
  'tv-mount',
  'shower-controls',
  'shower-stem',
];

/**
 * Ceiling-mounted object types
 * These objects hang from the ceiling and are typically not shown in wall elevations
 */
export const CEILING_MOUNTED_OBJECT_TYPES = [
  'ceiling-light',
  'ceiling-fan',
  // Exhaust fans and smoke / CO detectors from the Electrical tray.
  ...Object.keys(ELECTRICAL_DEVICES).filter(isCeilingElectricalDevice),
];

/**
 * Proximity threshold for ground-mounted objects (in meters)
 * Objects are shown if any edge is within this distance of the wall
 * 4 inches = 0.1016 meters
 */
export const GROUND_MOUNTED_PROXIMITY_THRESHOLD = 0.1016; // 4 inches in meters

/**
 * Check if an object type is ground-mounted.
 *
 * For toilets, the optional `doc` argument lets callers branch on the
 * per-doc `mountType` field — wall-hung toilets are NOT ground-mounted.
 *
 * @param {string} objectType - The type of object
 * @param {Object} [doc] - Optional fixture doc/instance (looks for `mountType`)
 * @returns {boolean}
 */
export function isGroundMounted(objectType, doc = null) {
  if (!objectType || typeof objectType !== 'string') return false;
  const normalizedType = objectType.toLowerCase();
  // Staged decor is photo dressing and never appears in elevations or
  // construction documents. Checked as EXACT membership of the derived
  // staging set, before the substring lists below — 'staging-art' would
  // otherwise match 'wall-art', and 'staging-plant-small' matches nothing
  // only by luck.
  if (isStagingType(normalizedType)) return false;
  if (normalizedType === 'toilet' && doc?.mountType === 'wall-hung') {
    return false;
  }
  return GROUND_MOUNTED_OBJECT_TYPES.some(
    (type) => normalizedType === type || normalizedType.includes(type)
  );
}

/**
 * Check if an object type is wall-mounted.
 *
 * For toilets, the optional `doc` argument lets callers branch on the
 * per-doc `mountType` field — wall-hung toilets ARE wall-mounted even
 * though 'toilet' isn't in the static WALL_MOUNTED_OBJECT_TYPES list.
 *
 * @param {string} objectType - The type of object
 * @param {Object} [doc] - Optional fixture doc/instance (looks for `mountType`)
 * @returns {boolean}
 */
export function isWallMounted(objectType, doc = null) {
  if (!objectType || typeof objectType !== 'string') return false;
  const normalizedType = objectType.toLowerCase();
  // Staged decor is photo dressing and never appears in elevations or
  // construction documents. Checked as EXACT membership of the derived
  // staging set, before the substring lists below — 'staging-art' would
  // otherwise match 'wall-art', and 'staging-plant-small' matches nothing
  // only by luck.
  if (isStagingType(normalizedType)) return false;
  if (normalizedType === 'toilet' && doc?.mountType === 'wall-hung') {
    return true;
  }
  return WALL_MOUNTED_OBJECT_TYPES.some(
    (type) => normalizedType === type || normalizedType.includes(type)
  );
}

/**
 * Check if an object type is ceiling-mounted
 * @param {string} objectType - The type of object
 * @returns {boolean}
 */
export function isCeilingMounted(objectType) {
  if (!objectType || typeof objectType !== 'string') return false;
  const normalizedType = objectType.toLowerCase();
  // Staged decor is photo dressing and never appears in elevations or
  // construction documents. Checked as EXACT membership of the derived
  // staging set, before the substring lists below — 'staging-art' would
  // otherwise match 'wall-art', and 'staging-plant-small' matches nothing
  // only by luck.
  if (isStagingType(normalizedType)) return false;
  return CEILING_MOUNTED_OBJECT_TYPES.some(
    (type) => normalizedType === type || normalizedType.includes(type)
  );
}
